import { useState } from 'react';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { FAQ } from '../components/FAQ';
import { CeramicProtectionInfographic } from '../components/CeramicProtectionInfographic';
import { PriceEstimator } from '../components/PriceEstimator';
import { Link } from 'react-router-dom';
import { 
  Shield, Droplets, Sun, Clock, 
  CheckCircle, Phone, ArrowRight, Star, Calculator
} from 'lucide-react';
import { ContactDropdown } from '../components/ContactDropdown';

export const CeramicCoatingPage = () => {
  const [isEstimatorOpen, setIsEstimatorOpen] = useState(false);

  const benefits = [
    { icon: <Shield className="w-6 h-6" />, title: '5+ Years Protection', description: 'Long-lasting ceramic shield protects your paint for years, not months.' },
    { icon: <Droplets className="w-6 h-6" />, title: 'Hydrophobic Finish', description: 'Water beads and rolls off, making washing easier and keeping your car cleaner longer.' },
    { icon: <Sun className="w-6 h-6" />, title: 'UV Protection', description: 'Blocks harmful UV rays that cause paint oxidation and fading.' },
    { icon: <Star className="w-6 h-6" />, title: 'Enhanced Gloss', description: 'Deep, mirror-like shine that turns heads wherever you go.' },
  ];

  const processSteps = [
    { step: '01', title: 'Wash & Decontamination', description: 'Thorough hand wash, iron removal, and clay bar treatment to ensure a perfectly clean surface.' },
    { step: '02', title: 'Paint Correction', description: 'Multi-stage polishing to remove swirls, scratches, and imperfections before coating.' },
    { step: '03', title: 'Surface Prep', description: 'IPA wipe-down to remove all oils and residues for optimal coating adhesion.' },
    { step: '04', title: 'Ceramic Application', description: 'Professional-grade ceramic coating applied panel by panel in controlled conditions.' },
    { step: '05', title: 'Curing', description: 'Proper cure time ensures maximum hardness and durability of the coating.' },
  ];

  const faqItems = [
    {
      question: 'How long does ceramic coating last in Fresno\'s climate?',
      answer: 'Our professional ceramic coatings are specifically chosen to withstand Central California\'s hot summers and mild winters. Depending on the package, your coating will last 2-5+ years with proper maintenance. The Valley\'s dry climate is actually ideal for ceramic coatings!'
    },
    {
      question: 'What is the cost of ceramic coating in Fresno?',
      answer: 'Ceramic coating prices in Fresno range from $500-$2,000+ depending on vehicle size and coating tier. Our packages include paint correction, which is essential for optimal results. Contact us at 559-880-1818 for a free quote.'
    },
    {
      question: 'Is ceramic coating worth it for my daily driver?',
      answer: 'Absolutely! Ceramic coating is especially valuable for daily drivers in Fresno. It protects against Valley dust, agricultural fallout, bird droppings, and UV damage. Plus, the hydrophobic properties make washing much easier - you\'ll spend less time cleaning your car.'
    },
    {
      question: 'How do I maintain ceramic coating?',
      answer: 'Maintenance is simple: wash regularly with pH-neutral soap, avoid automatic car washes with harsh brushes, and apply a ceramic boost spray every few months. We provide detailed care instructions and recommend maintenance washes to keep your coating performing at its best.'
    },
    {
      question: 'Do you offer ceramic coating in Clovis and Madera?',
      answer: 'Yes! We provide mobile ceramic coating services throughout Fresno, Clovis, Madera, Chowchilla, and surrounding Central California areas. We bring all professional equipment to your location for your convenience.'
    },
    {
      question: 'How long does ceramic coating installation take?',
      answer: 'A professional ceramic coating installation typically takes 1-3 days depending on the vehicle\'s condition and the coating package selected. This includes thorough preparation, paint correction, and proper curing time for optimal results.'
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Ceramic Coating Service Fresno",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Noble Detailing",
      "telephone": "559-880-1818",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Fresno",
        "addressRegion": "CA"
      }
    },
    "areaServed": ["Fresno", "Madera", "Clovis", "Chowchilla"],
    "description": "Professional ceramic coating services in Fresno, CA. Long-lasting paint protection with hydrophobic finish and UV resistance."
  };

  return (
    <>
      <SEOHead
        title="Ceramic Coating Fresno | Professional Paint Protection | Noble Detailing"
        description="Professional ceramic coating in Fresno, Madera & Clovis. 5+ year paint protection with hydrophobic finish. Certified installers serving Central California. Call 559-880-1818 for a free quote."
        keywords="ceramic coating fresno, ceramic coating madera, ceramic coating clovis, paint protection fresno, car coating fresno, ceramic coat near me, best ceramic coating fresno, professional ceramic coating central california"
        canonical="https://nobledetailing.com/ceramic-coating-fresno"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center pt-20" data-testid="ceramic-hero">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1694678505383-676d78ea3b96?w=1920&q=80"
            alt="Ceramic coating water beading on car paint in Fresno"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
              <span className="text-gray-600">/</span>
              <span className="text-cyan-400">Ceramic Coating</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight uppercase tracking-wide">
              Ceramic Coating<br />
              <span className="text-cyan-400">Fresno & Central CA</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Protect your investment with professional ceramic coating. Our certified installers deliver <strong>5+ years of paint protection</strong> with a hydrophobic, self-cleaning finish. Serving <strong>Fresno, Madera, Clovis, Chowchilla</strong> and surrounding areas.
            </p>

            <div className="flex flex-wrap gap-4">
              <ContactDropdown 
                label="Get Free Quote" 
                className="btn-primary"
                testId="ceramic-cta"
              />
              <button 
                onClick={() => setIsEstimatorOpen(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" /> Price Estimate
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Price Estimator Modal */}
      <PriceEstimator 
        isOpen={isEstimatorOpen} 
        onClose={() => setIsEstimatorOpen(false)} 
        defaultService="ceramic"
      />

      {/* Benefits Section */}
      <section className="section-padding bg-[#0a0a0a]" data-testid="ceramic-benefits">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Why Choose Ceramic Coating?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Ceramic coating provides superior protection compared to wax or sealants, lasting years instead of weeks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-neutral-900/50 border border-white/5 hover:border-cyan-500/30 transition-all"
              >
                <div className="w-14 h-14 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mx-auto mb-6">
                  {benefit.icon}
                </div>
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wider mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Protection Infographic */}
      <CeramicProtectionInfographic />

      {/* Process Section */}
      <section className="section-padding" data-testid="ceramic-process">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-4">
              Our Ceramic Coating Process
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Professional ceramic coating requires proper preparation and application. Here's how we ensure lasting results for Fresno area vehicles.
            </p>
          </div>

          <div className="space-y-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex gap-8 items-start"
              >
                <div className="font-accent text-5xl font-bold text-cyan-500/30 w-20 flex-shrink-0">
                  {step.step}
                </div>
                <div className="flex-grow pb-8 border-b border-white/10">
                  <h3 className="font-heading text-xl font-semibold uppercase tracking-wider mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-padding bg-[#0a0a0a]" data-testid="ceramic-areas">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-6">
                Ceramic Coating Service Areas
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                Noble Detailing provides professional ceramic coating services throughout Central California. We bring our equipment to your location for convenient, hassle-free service.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {['Fresno', 'Madera', 'Clovis', 'Chowchilla', 'Sanger', 'Selma', 'Visalia', 'Hanford'].map((city) => (
                  <div key={city} className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-500" />
                    <span>{city}, CA</span>
                  </div>
                ))}
              </div>

              <ContactDropdown 
                label="Contact Us" 
                className="btn-primary"
                testId="ceramic-service-area-contact"
              />
            </div>

            <div className="bg-neutral-900 border border-white/10 p-8">
              <h3 className="font-heading text-xl font-semibold uppercase tracking-wider mb-6">
                What's Included
              </h3>
              <ul className="space-y-4">
                {[
                  'Full exterior decontamination wash',
                  'Clay bar treatment',
                  'Paint correction (swirl removal)',
                  'Professional ceramic coating application',
                  'Interior wipe-down',
                  'Window cleaning',
                  'Tire dressing',
                  'Aftercare instructions & products'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ items={faqItems} title="Ceramic Coating FAQ - Fresno" />

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-b from-[#0a0a0a] to-black" data-testid="ceramic-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-wider mb-6">
            Ready to Protect Your Vehicle?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Get a free quote for professional ceramic coating in Fresno, Madera, Clovis, and surrounding areas. Our certified installers are ready to give your car the protection it deserves.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <ContactDropdown 
              label="Contact Us" 
              className="btn-primary"
              testId="ceramic-bottom-contact"
            />
            <Link 
              to="/#packages"
              className="btn-secondary"
            >
              View Packages
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
